import React from "react";
import { Toaster } from "react-hot-toast";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/auth/LoginPage";
import SelectRegistration from "./pages/auth/SelectRegistration";
import StudentRegistration from "./pages/auth/StudentRegistration";
import ClubRegistration from "./pages/auth/ClubRegistration";
import RegistrationStatus from "./pages/auth/RegistrationStatus";
import ClubSelectPage from "./pages/auth/ClubSelectPage";
import PaymentMethod from "./pages/auth/PaymentMethod";
import PaymentSuccess from "./pages/auth/PaymentSuccess";
import CoachDashboard from "./pages/coach/CoachDashboard";
import StudentDashboard from "./pages/student/StudentDashboard";
import IndependentDashboard from "./pages/independent/IndependentDashboard";
// Back-office (system user / RBAC) area — token-authenticated, separate from
// the member OTP flow. Gated by AuthProvider + ProtectedRoute.
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminLogin from "./pages/admin/AdminLogin";
import ForgotPassword from "./pages/admin/ForgotPassword";
import ChangePassword from "./pages/admin/ChangePassword";
import AdminLayout from "./pages/admin/AdminLayout";
import { ADMIN_NAV } from "./config/adminNav";
import { FEATURE_FLAGS } from "./config/featureFlags";

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" reverseOrder={false} />
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/select-registration" element={<SelectRegistration />} />
          <Route path="/student-registration" element={<StudentRegistration />} />
          {/* Public club self-registration is switched off via
              FEATURE_FLAGS.publicClubRegistration — redirect instead of
              rendering the form (same for the legacy /clubregister below). */}
          <Route
            path="/club-registration"
            element={FEATURE_FLAGS.publicClubRegistration ? <ClubRegistration /> : <Navigate to="/student-registration" replace />}
          />
          <Route path="/registration-status" element={<RegistrationStatus />} />
          <Route path="/select-club" element={<ClubSelectPage />} />
          <Route path="/payment-method" element={<PaymentMethod />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/coach-dashboard" element={<CoachDashboard />} />
          <Route path="/student-dashboard" element={<StudentDashboard />} />
          <Route path="/independent-dashboard" element={<IndependentDashboard />} />
          {/* Legacy paths kept so existing links/bookmarks still work */}
          <Route path="/register" element={<StudentRegistration />} />
          <Route
            path="/clubregister"
            element={FEATURE_FLAGS.publicClubRegistration ? <ClubRegistration /> : <Navigate to="/student-registration" replace />}
          />

          {/* ── Back-office / admin (RBAC) ── */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/admin/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            {/* One <Route> per ADMIN_NAV entry — the same module registry that
                drives the sidebar (see AdminLayout.jsx), so a module's route
                access and its nav visibility can never drift apart. */}
            {ADMIN_NAV.map(({ key, path, permission, requireSuperAdmin, Component }) => (
              <Route
                key={key}
                {...(path === "" ? { index: true } : { path })}
                element={
                  <ProtectedRoute permission={permission} requireSuperAdmin={requireSuperAdmin}>
                    <Component />
                  </ProtectedRoute>
                }
              />
            ))}
            {/* Unknown /admin/* paths — including modules currently switched
                off via config/featureFlags.js (e.g. /admin/bookings) — bounce
                back to the admin dashboard instead of an empty layout. */}
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;